<template>
  <td>
    <!-- CASO: INSUMOS -->
    <template v-if="tipo === 'insumos'">
      <!-- Si hay cambios en insumos, mostramos la tablita con diffs -->
      <div
        v-if="hasChanges"
        class="py-1"
        style="min-width: 260px; max-width: 320px"
      >
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center">
            <v-chip
              x-small
              color="amber lighten-4"
              class="amber--text text--darken-4 font-weight-bold mr-1"
            >
              <v-icon left x-small color="amber darken-4">mdi-pencil</v-icon>
              Modificado
            </v-chip>
            <span :class="['text-caption font-weight-bold', themeTextClass]">
              {{ (item.Catalogo && item.Catalogo.length) || 0 }} insumo(s)
            </span>
          </div>
          <v-btn
            v-if="currentStep === 0"
            x-small
            depressed
            :color="themeLightColor"
            :class="`${themeTextClass} font-weight-bold px-1`"
            @click="$emit('edit')"
            title="Editar insumos"
            style="height: 20px; font-size: 10px"
          >
            <v-icon x-small class="mr-1">mdi-pencil-outline</v-icon>
            Editar
          </v-btn>
        </div>
        <template v-if="diffList.length === 0">
          <div class="d-flex align-center grey--text text-caption py-1">
            <v-icon x-small color="grey lighten-1" class="mr-1">
              mdi-alert-circle-outline
            </v-icon>
            <span style="font-size: 11px">Sin insumos</span>
          </div>
        </template>
        <template v-else>
          <div
            class="d-flex flex-column pr-1"
            style="max-height: 96px; overflow-y: auto; gap: 2px"
          >
            <div
              v-for="(diff, dIdx) in diffList"
              :key="'diff-ins-' + dIdx"
              class="text-caption d-flex align-center justify-space-between px-2 py-0.5 rounded"
              :style="getDiffConfig(diff.status, 'insumo').rowStyle"
            >
              <div
                class="d-flex align-center text-truncate mr-1"
                style="max-width: 170px"
              >
                <v-icon
                  x-small
                  :color="getDiffConfig(diff.status, 'insumo').iconColor"
                  class="mr-1 flex-shrink-0"
                  style="font-size: 13px"
                >
                  {{ getDiffConfig(diff.status, 'insumo').icon }}
                </v-icon>
                <span
                  :class="[
                    'text-truncate font-weight-medium',
                    getDiffConfig(diff.status, 'insumo').textClass
                  ]"
                  :style="
                    diff.status === 'deleted'
                      ? 'text-decoration: line-through; opacity: 0.85;'
                      : ''
                  "
                >
                  {{ diff.displayName }}
                </span>
              </div>
              <div class="d-flex align-center flex-shrink-0">
                <span
                  v-if="diff.status === 'modified' && diff.prevCant !== null"
                  class="grey--text text-caption mr-1"
                  style="text-decoration: line-through; font-size: 10px"
                >
                  {{ diff.prevCant }}
                </span>
                <span
                  :class="[
                    'font-weight-bold',
                    getDiffConfig(diff.status, 'insumo').cantClass
                  ]"
                  :style="[
                    { fontSize: '10.5px' },
                    diff.status === 'deleted'
                      ? { textDecoration: 'line-through', opacity: 0.85 }
                      : {}
                  ]"
                >
                  {{ diff.status === 'added' ? '+' : '' }}{{ diff.cant }} {{ diff.um }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Si NO hay cambios, mostramos el botón / chip de 'Sin cambios' de antes -->
      <div
        v-else
        class="d-flex align-center justify-center pt-1"
        style="min-width: 140px"
      >
        <v-btn
          v-if="currentStep === 0"
          small
          depressed
          :color="themeLightColor"
          :class="`${themeTextClass} font-weight-bold px-2`"
          @click="$emit('edit')"
          title="Clic para editar insumos"
        >
          <v-icon left small>mdi-silverware-fork-knife</v-icon>
          {{ (item.Catalogo && item.Catalogo.length) || 0 }}
          <span
            class="caption grey--text text--darken-1 font-weight-regular ml-1"
            style="font-size: 11px"
          >
            (Sin cambios)
          </span>
        </v-btn>
        <v-chip
          v-else
          x-small
          color="grey lighten-4"
          class="grey--text text--darken-3 font-weight-medium"
        >
          <v-icon left x-small color="grey darken-2">mdi-silverware-fork-knife</v-icon>
          {{ (item.Catalogo && item.Catalogo.length) || 0 }} insumos (Sin cambios)
        </v-chip>
      </div>
    </template>

    <!-- CASO: MODIFICADORES -->
    <template v-else-if="tipo === 'modificadores'">
      <!-- Si hay cambios en modificadores, mostramos la tablita con diffs -->
      <div
        v-if="hasChanges"
        class="py-1"
        style="min-width: 250px; max-width: 310px"
      >
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center">
            <v-chip
              x-small
              color="purple lighten-4"
              class="purple--text text--darken-4 font-weight-bold mr-1"
            >
              <v-icon left x-small color="purple darken-4">mdi-pencil</v-icon>
              Modificado
            </v-chip>
            <span class="text-caption font-weight-bold purple--text text--darken-3">
              {{ (item.Modificadores && item.Modificadores.length) || 0 }} grupo(s)
            </span>
          </div>
          <v-btn
            v-if="currentStep === 0"
            x-small
            depressed
            color="purple lighten-5"
            class="purple--text text--darken-3 font-weight-bold px-1"
            @click="$emit('edit')"
            title="Editar modificadores"
            style="height: 20px; font-size: 10px"
          >
            <v-icon x-small class="mr-1">mdi-pencil-outline</v-icon>
            Editar
          </v-btn>
        </div>
        <template v-if="diffList.length === 0">
          <div class="d-flex align-center grey--text text-caption py-1">
            <v-icon x-small color="grey lighten-1" class="mr-1">
              mdi-alert-circle-outline
            </v-icon>
            <span style="font-size: 11px">Sin modificadores</span>
          </div>
        </template>
        <template v-else>
          <div
            class="d-flex flex-column pr-1"
            style="max-height: 96px; overflow-y: auto; gap: 2px"
          >
            <div
              v-for="(diff, modIdx) in diffList"
              :key="'diff-mod-' + modIdx"
              class="text-caption d-flex align-center justify-space-between px-2 py-0.5 rounded cursor-pointer"
              :style="getDiffConfig(diff.status, 'modificador').rowStyle"
              @click="currentStep === 0 ? $emit('edit') : null"
            >
              <div
                class="d-flex align-center text-truncate mr-1"
                style="max-width: 170px"
              >
                <v-icon
                  x-small
                  :color="getDiffConfig(diff.status, 'modificador').iconColor"
                  class="mr-1 flex-shrink-0"
                  style="font-size: 13px"
                >
                  {{ getDiffConfig(diff.status, 'modificador').icon }}
                </v-icon>
                <span
                  :class="[
                    'text-truncate font-weight-medium',
                    getDiffConfig(diff.status, 'modificador').textClass
                  ]"
                  :style="
                    diff.status === 'deleted'
                      ? 'text-decoration: line-through; opacity: 0.85;'
                      : ''
                  "
                >
                  {{ diff.name }}
                </span>
              </div>
              <div class="d-flex align-center flex-shrink-0">
                <span
                  v-if="diff.status === 'modified' && diff.prevOrnName"
                  class="grey--text text-caption mr-1"
                  style="text-decoration: line-through; font-size: 9.5px"
                >
                  [{{ diff.prevOrnName }}]
                </span>
                <span
                  v-if="diff.ornName"
                  :class="[
                    'text-caption font-weight-bold flex-shrink-0',
                    diff.status === 'added'
                      ? 'green--text text--darken-3'
                      : diff.status === 'deleted'
                      ? 'red--text text--darken-3'
                      : themeTextClass
                  ]"
                  :style="[
                    { fontSize: '10px' },
                    diff.status === 'deleted'
                      ? { textDecoration: 'line-through', opacity: 0.85 }
                      : {}
                  ]"
                >
                  [{{ diff.ornName }}]
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Si NO hay cambios, mostramos el botón / chip de 'Sin cambios' de antes -->
      <div
        v-else
        class="d-flex align-center justify-center pt-1"
        style="min-width: 140px"
      >
        <v-btn
          v-if="currentStep === 0"
          small
          depressed
          color="purple lighten-5"
          class="purple--text text--darken-2 font-weight-bold px-2"
          @click="$emit('edit')"
          title="Clic para editar modificadores"
        >
          <v-icon left small>mdi-tune</v-icon>
          {{ (item.Modificadores && item.Modificadores.length) || 0 }}
          <span
            class="caption grey--text text--darken-1 font-weight-regular ml-1"
            style="font-size: 11px"
          >
            (Sin cambios)
          </span>
        </v-btn>
        <v-chip
          v-else
          x-small
          color="grey lighten-4"
          class="grey--text text--darken-3 font-weight-medium"
        >
          <v-icon left x-small color="purple darken-2">mdi-tune</v-icon>
          {{ (item.Modificadores && item.Modificadores.length) || 0 }} grupos (Sin cambios)
        </v-chip>
      </div>
    </template>
  </td>
</template>

<script>
import { getDiffConfig } from "@/clases/Restaurante/ReplicadorRecetas";

export default {
  name: "CeldaResumenCambios",
  props: {
    tipo: {
      type: String,
      required: true,
      validator: (val) => ["insumos", "modificadores"].includes(val)
    },
    item: {
      type: Object,
      required: true
    },
    diffList: {
      type: Array,
      default: () => []
    },
    hasChanges: {
      type: Boolean,
      default: false
    },
    currentStep: {
      type: Number,
      default: 0
    },
    themeLightColor: {
      type: String,
      default: "blue lighten-5"
    },
    themeTextClass: {
      type: String,
      default: "blue--text text--darken-4"
    }
  },
  methods: {
    getDiffConfig(status, entityType) {
      return getDiffConfig(status, entityType);
    }
  }
};
</script>
